import React, { MutableRefObject, ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { makeStyles, useTheme } from '@mui/styles'

import clsx from 'clsx'

const useStyles = makeStyles(() => ({
  svgIcon: {
    display: 'inline-flex',
  },
}))

interface SVGIconProps {
  id?: string,
  svg: ReactNode | string | React.ComponentType<React.SVGProps<SVGSVGElement>>,
  width?: string | number,
  height?: string | number,
  fill?: string
  customClass?: string
  className?: string,
  onClick?: (e?: Event) => void
  overflow?: string,
  style?: React.CSSProperties
}

const SVGIcon = (props: SVGIconProps, ref: React.Ref<SVGSVGElement>) => {
  const classes = useStyles()
  const theme = useTheme()
  const {
    svg: SvgUrlOrComponent,
    width = '100%',
    height = '100%',
    fill = theme?.icons?.base,
    customClass,
    className,
    ...otherProps
  } = props

  const innerRef = useRef<SVGSVGElement>(null)
  // Keep the node locally — the inline-URL branch below writes into it — and pass it on to the
  // forwarded ref. Callers like MUI's Tooltip hand in a callback ref and stay closed until they
  // receive the node, so the ref has to reach the rendered element in both branches.
  const handleRef = useCallback((node: SVGSVGElement) => {
    innerRef.current = node
    if (typeof ref === 'function') {
      ref(node)
    } else if (ref) {
      (ref as MutableRefObject<SVGSVGElement>).current = node
    }
  }, [ref])
  const [content, setContent] = useState<string>()
  const [attributes, setAttributes] = useState<{[key:string]: string}>()

  useEffect(() => {
    if (typeof SvgUrlOrComponent === 'string' && SvgUrlOrComponent.startsWith('/')) {
      const url = SvgUrlOrComponent
      fetch(url, { cache: 'force-cache' })
        .then((response) => {
          if (!response.ok) throw new Error('SVG fetch error')
          return response.text()
        })
        .then((contentText) => {
          innerRef.current.innerHTML = contentText

          const tempDiv = document.createElement('div')
          tempDiv.innerHTML = contentText
          const tempSVG = tempDiv.children[0]

          const currentProps = Object.keys(props)
          setAttributes(Array.from(tempSVG.attributes).reduce((acc, attr) => {
            if (currentProps.includes(attr.name)) return acc
            acc[attr.name] = attr.value
            return acc
          }, {}))

          setContent(tempSVG.innerHTML)
        })
        .catch(console.error)
    }
  }, [SvgUrlOrComponent])

  useEffect(() => {
    if (innerRef.current && attributes) {
      Object.keys(attributes).forEach((key) => {
        innerRef.current.setAttribute(key, attributes[key])
      })
    }
  }, [attributes])

  if (!SvgUrlOrComponent) {
    console.warn('SvgUrlOrComponent is not defined')
    return null
  }

  // Imported .svg files are compiled by @svgr/webpack with `ref: true`, so they forward the ref
  // to their own <svg> element — the intrinsic 'svg' below does the same for inline SVG urls.
  const Component = typeof SvgUrlOrComponent === 'string' ? 'svg' : SvgUrlOrComponent

  return (
    <Component
      ref={handleRef}
      className={clsx(classes.svgIcon, customClass, className)}
      width={width}
      height={height}
      fill={fill}
      dangerouslySetInnerHTML={typeof SvgUrlOrComponent === 'string' ? { __html: content } : undefined}
      {...otherProps}
    />
  )
}

const SVGIconForwared = React.forwardRef(SVGIcon)
export default React.memo(SVGIconForwared)
